import React, { useEffect, useState, lazy, Suspense } from 'react'
import { Helmet } from 'react-helmet-async'
import { Button } from '@/components/ui/button.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx'
import { ArrowRight, ShieldCheck, CheckCircle, Clock, FileCheck, Lock, HeartHandshake } from 'lucide-react'
import { trackCTAClick, trackConversion } from '../utils/analytics.js'

const VerificationFormEnhanced = lazy(() => import('../components/VerificationFormEnhanced.jsx'))

/**
 * Home Page Component
 * 
 * The main landing page for Authentipet. Features:
 * - Hero section with value proposition and CTA
 * - Pricing comparison
 * - "How it Works" steps
 * - Benefits and trust indicators
 * - ESA Verification modal trigger
 * 
 * Optimizations:
 * - Lazy loading of the verification modal (VerificationFormEnhanced)
 * - SEO meta tags via React Helmet
 * - Semantic HTML structure
 * - Analytics tracking for CTAs
 * 
 * @returns {JSX.Element} The rendered home page
 */
function Home() {
  const [isVerificationStarted, setIsVerificationStarted] = useState(false)
  const [selectedServiceType, setSelectedServiceType] = useState('standard')
  // Preload the verification form chunk to avoid suspense delay
  useEffect(() => {
    import('../components/VerificationFormEnhanced.jsx').catch(() => { })
  }, [])

  const handleVerificationStart = () => {
    trackCTAClick('start_verification', 'home_page', 'hero_section')
    setIsVerificationStarted(true)
  }

  const handleVerificationSuccess = () => {
    // Track conversion (value will be updated based on selected plan in form)
    trackConversion('verification_started', 49.99, 'USD')
    setIsVerificationStarted(false)
  }

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Authentipet',
    url: 'https://authentipets.com',
    description: 'AI-powered ESA letter authenticity scoring service'
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'ESA Letter Authenticity Scoring',
    provider: { '@type': 'Organization', name: 'Authentipet', url: 'https://authentipets.com' },
    description: 'AI-powered scoring service that evaluates ESA (Emotional Support Animal) letters for authenticity and compliance. We score your letter and report results to you and your property manager.',
    serviceType: 'ESA Letter Scoring',
    offers: [
      { '@type': 'Offer', name: 'Standard Scoring', price: '49.99', priceCurrency: 'USD', description: 'AI-powered authenticity scoring, 3-5 business days' },
      { '@type': 'Offer', name: 'Rush Scoring', price: '99.99', priceCurrency: 'USD', description: 'Priority AI + human review, within 24 hours' }
    ]
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Authentipet — AI-Powered ESA Letter Scoring | Get Your Authenticity Score</title>
        <meta name="description" content="Upload your ESA letter and get an AI-powered authenticity score report. Fast, secure scoring for tenants and property managers. Standard $49.99 | Rush $99.99." />
        <meta name="keywords" content="ESA verification score, emotional support animal letter, ESA letter scoring, landlord ESA verification, ESA authenticity score" />
        <link rel="canonical" href="https://authentipets.com/" />
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <main>
        {/* Hero Section */}
        <section className="relative ap-gradient-primary py-20 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="space-y-4">
                  <Badge className="bg-white text-ap-blurple hover:bg-white/90 ap-glow-blurple">
                    TRUSTED ESA VERIFICATION SCORING
                  </Badge>
                  <h1 className="ap-hero-title text-white">
                    <span className="text-ap-blurple">SCORE YOUR <span className="text-ap-pink drop-shadow-[0_0_10px_rgba(192,0,84,0.5)]">ESA</span> LETTER</span> FOR AUTHENTICITY
                  </h1>
                  <p className="text-white/90 text-xl leading-relaxed">
                    Get your Emotional Support Animal letter scored for authenticity by our AI-powered system. We score your documentation and report back to you and your property manager. <strong className="text-ap-pink">Rush scoring available in 24 hours!</strong>
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="bg-ap-blurple hover:bg-ap-blurple/90 text-white ap-button-text text-lg px-8 py-6 ap-glow-blurple"
                    onClick={handleVerificationStart}
                  >
                    GET YOUR SCORE NOW
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <div className="flex items-center space-x-2 text-white">
                    <ShieldCheck className="h-5 w-5" />
                    <span className="text-sm font-medium">100% HIPAA Compliant</span>
                  </div>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap gap-4 text-white/80 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span>AI-Powered Scoring</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span>24-Hour Turnaround</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span>Money-Back Guarantee</span>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block">
                {/* Note: sizes attribute helps browser determine which source to use - image displays at max 512px on lg+ screens */}
                <picture>
                  <source srcSet="/verification-hero.webp" type="image/webp" sizes="(min-width: 1024px) 512px, 0px" />
                  <img
                    src="/verification-hero.png"
                    alt="ESA Letter Scoring - Happy pet owner with dog"
                    className="w-full h-auto max-w-lg mx-auto"
                    width="512"
                    height="512"
                    loading="eager"
                    fetchpriority="high"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16 ap-gradient-dark" id="pricing">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="ap-section-title text-center text-white mb-8">CHOOSE YOUR SCORING SPEED</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Standard Option */}
                <div className="bg-ap-green rounded-2xl shadow-lg p-8 border-2 border-ap-green/50 hover:border-white/50 transition-all hover:scale-105 duration-300 cursor-pointer" style={{ boxShadow: '0 0 30px rgba(76, 245, 160, 0.4), 0 0 60px rgba(76, 245, 160, 0.2), inset 0 0 30px rgba(255, 255, 255, 0.1)' }} onClick={() => { setSelectedServiceType('standard'); handleVerificationStart() }}>
                  <div className="text-center">
                    <h3 className="ap-card-title text-ap-dark-green mb-2">STANDARD</h3>
                    <div className="ap-hero-title text-ap-dark-green mb-2">$49.99</div>
                    <Badge className="mb-4 bg-ap-dark-green/10 text-ap-dark-green hover:bg-ap-dark-green/20">AI-POWERED VERIFICATION</Badge>
                    <p className="text-ap-dark-green/80 mb-4">3-5 business days</p>
                    <ul className="text-left space-y-2 text-sm text-ap-dark-green/80 mb-6">
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-dark-green" /> 95% AI Accuracy Check</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-dark-green" /> Professional review</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-dark-green" /> Authenticity score report</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-dark-green" /> 1-year validity</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-dark-green" /> Email delivery</li>
                    </ul>
                  </div>
                </div>

                {/* Rush Option */}
                <div className="bg-ap-green rounded-2xl shadow-lg p-8 border-2 border-ap-pink relative hover:scale-105 transition-all duration-300 cursor-pointer" style={{ boxShadow: '0 0 30px rgba(76, 245, 160, 0.4), 0 0 60px rgba(76, 245, 160, 0.2), inset 0 0 30px rgba(255, 255, 255, 0.1)' }} onClick={() => { setSelectedServiceType('rush'); handleVerificationStart() }}>
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-ap-pink text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">MOST POPULAR</div>
                  </div>
                  <div className="text-center">
                    <h3 className="ap-card-title text-ap-dark-green mb-2">RUSH</h3>
                    <div className="ap-card-title text-cta-pink mb-2">$99.99</div>
                    <Badge className="mb-4 bg-ap-pink/10 text-ap-pink hover:bg-ap-pink/20">PRIORITY AI + HUMAN REVIEW</Badge>
                    <p className="text-ap-dark-green/80 mb-4">Within 24 hours</p>
                    <ul className="text-left space-y-2 text-sm text-ap-dark-green/80 mb-6">
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-pink" /> Everything in Standard</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-pink" /> Claude AI + Human Review</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-pink" /> Priority processing</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-pink" /> SMS notifications</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-ap-pink" /> 24-hour guarantee</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 ap-gradient-dark">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="ap-section-title text-white mb-4">
                HOW ESA LETTER SCORING WORKS
              </h2>
              <p className="ap-body-text text-xl text-white/80 max-w-3xl mx-auto">
                Our simple 3-step process ensures your ESA letter meets all legal requirements
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="text-center p-8 bg-ap-blurple/10 border-2 border-ap-blurple/30 hover:border-ap-blurple transition-colors ap-glow-blurple backdrop-blur rounded-xl">
                <div className="mx-auto w-16 h-16 bg-ap-blurple/20 rounded-full flex items-center justify-center mb-4 ap-glow-blurple">
                  <FileCheck className="h-8 w-8 text-ap-blurple" />
                </div>
                <h3 className="ap-card-title text-white mb-2">1. UPLOAD YOUR LETTER</h3>
                <p className="ap-body-text text-white/80">
                  Securely upload your ESA letter and provide basic information about your animal
                </p>
              </div>

              <div className="text-center p-8 bg-ap-blurple/10 border-2 border-ap-blurple/30 hover:border-ap-blurple transition-colors ap-glow-blurple backdrop-blur rounded-xl">
                <div className="mx-auto w-16 h-16 bg-ap-blurple/20 rounded-full flex items-center justify-center mb-4 ap-glow-blurple">
                  <ShieldCheck className="h-8 w-8 text-ap-blurple" />
                </div>
                <h3 className="ap-card-title text-white mb-2">2. EXPERT REVIEW</h3>
                <p className="ap-body-text text-white/80">
                  Our AI-powered system reviews your documentation for compliance and authenticity
                </p>
              </div>

              <div className="text-center p-8 bg-ap-blurple/10 border-2 border-ap-blurple/30 hover:border-ap-blurple transition-colors ap-glow-blurple backdrop-blur rounded-xl">
                <div className="mx-auto w-16 h-16 bg-ap-blurple/20 rounded-full flex items-center justify-center mb-4 ap-glow-blurple">
                  <CheckCircle className="h-8 w-8 text-ap-blurple" />
                </div>
                <h3 className="ap-card-title text-white mb-2">3. GET YOUR SCORE</h3>
                <p className="ap-body-text text-white/80">
                  Receive your authenticity score report within 24 hours, shared with you and your property manager
                </p>
              </div>
            </div>

            <div className="text-center">
              <Button
                size="lg"
                className="bg-ap-blurple hover:bg-ap-blurple/90 text-white ap-button-text text-lg px-8 py-4 ap-glow-blurple"
                onClick={handleVerificationStart}
              >
                GET STARTED NOW
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 ap-gradient-primary">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="ap-section-title text-white mb-4">
                WHY CHOOSE AUTHENTIPET?
              </h2>
              <p className="ap-body-text text-xl text-white/80 max-w-3xl mx-auto">
                We're committed to making ESA letter scoring simple, fast, and reliable
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur p-6 rounded-lg border-2 border-white/20 hover:border-white/40 transition-colors">
                <Clock className="h-12 w-12 text-ap-blurple mb-4" />
                <h3 className="ap-card-title text-white mb-2">24-Hour Turnaround</h3>
                <p className="ap-body-text text-white/80">Get your authenticity score report in as little as 24 hours, guaranteed.</p>
              </div>

              <div className="bg-white/10 backdrop-blur p-6 rounded-lg border-2 border-white/20 hover:border-white/40 transition-colors">
                <Lock className="h-12 w-12 text-ap-blurple mb-4" />
                <h3 className="ap-card-title text-white mb-2">100% Secure & Private</h3>
                <p className="ap-body-text text-white/80">Your information is protected with bank-level encryption and HIPAA compliance.</p>
              </div>

              <div className="bg-white/10 backdrop-blur p-6 rounded-lg border-2 border-white/20 hover:border-white/40 transition-colors">
                <HeartHandshake className="h-12 w-12 text-ap-blurple mb-4" />
                <h3 className="ap-card-title text-white mb-2">Expert Scoring</h3>
                <p className="ap-body-text text-white/80">All letters are scored for authenticity using advanced AI and expert analysis.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 ap-gradient-primary">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="ap-section-title text-white mb-6">
                READY TO SCORE YOUR ESA LETTER?
              </h2>
              <p className="ap-body-text text-xl text-white/90 mb-8">
                Join thousands of satisfied customers who&apos;ve scored their ESA documentation for authenticity.
              </p>

              <Button
                onClick={handleVerificationStart}
                size="lg"
                className="w-full max-w-md bg-ap-blurple hover:bg-ap-blurple/90 text-white ap-button-text text-lg py-6 ap-glow-blurple"
              >
                VERIFY MY ESA LETTER NOW
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-white/60 text-sm mt-4">
                Powered by advanced AI for faster results. 100% money-back guarantee.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Verification Dialog */}
      <Dialog open={isVerificationStarted} onOpenChange={setIsVerificationStarted}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-ap-green border border-white/20 text-ap-dark-green">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-ap-dark-green text-center mb-2">Score Your ESA Letter</DialogTitle>
            <p className="text-ap-dark-green/90 mt-1 text-center">Get your Emotional Support Animal letter scored for authenticity with AI-powered analysis. Fast, secure, and HIPAA-compliant.</p>
          </DialogHeader>
          <div className="pt-4">
            <Suspense fallback={<div className="py-10 text-center text-ap-dark-green flex flex-col items-center gap-4"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ap-dark-green/50 border-t-ap-dark-green" />Loading verification form...</div>}>
              <VerificationFormEnhanced
                initialServiceType={selectedServiceType}
                onSuccess={handleVerificationSuccess}
              />
            </Suspense>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Home